type DrawerSide = 'shelf' | 'companion';

interface CloseOptions {
  restoreFocus?: boolean;
  focusReader?: boolean;
}

interface DrawerElements {
  app: HTMLElement;
  reader: HTMLElement;
  backdrop: HTMLButtonElement;
  panels: Record<DrawerSide, HTMLElement>;
  triggers: Record<DrawerSide, HTMLButtonElement>;
  closes: Record<DrawerSide, HTMLButtonElement>;
  media: MediaQueryList;
}

let elements: DrawerElements | null = null;
let active: DrawerSide | null = null;
let opener: HTMLElement | null = null;
let scrollStyles: { body: string; page: string } | null = null;
let focusRequest = 0;

const SIDES: DrawerSide[] = ['shelf', 'companion'];
const FOCUSABLE = 'button, a[href], input, select, textarea, [tabindex]';

function el(id: string): HTMLElement { return document.getElementById(id)!; }

function hasModal(): boolean {
  return !!document.querySelector('dialog[open]') || !el('authMask').hidden;
}

function visible(node: HTMLElement): boolean {
  return node.isConnected && !node.hidden && !node.closest('[inert], [hidden]') && node.getClientRects().length > 0;
}

function focusables(panel: HTMLElement): HTMLElement[] {
  return [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)]
    .filter((node) => node.tabIndex >= 0 && !node.matches(':disabled') && visible(node));
}

function focusInside(target?: HTMLElement) {
  if (!elements || !active || hasModal()) return;
  const panel = elements.panels[active];
  const candidate = target && panel.contains(target) && !target.matches(':disabled') && visible(target)
    ? target : elements.closes[active];
  candidate.focus({ preventScroll: true });
}

function lockScroll(lock: boolean) {
  const page = el('page');
  if (lock && !scrollStyles) {
    scrollStyles = { body: document.body.style.overflow, page: page.style.overflowY };
    document.body.style.overflow = 'hidden';
    page.style.overflowY = 'hidden';
  } else if (!lock && scrollStyles) {
    document.body.style.overflow = scrollStyles.body;
    page.style.overflowY = scrollStyles.page;
    scrollStyles = null;
  }
}

function hideReadingPanels() {
  el('displayPanel').hidden = true;
  el('displayBtn').classList.remove('on');
  for (const id of ['floatbar', 'markbar']) el(id).hidden = true;
}

function sync() {
  if (!elements) return;
  for (const side of SIDES) {
    const panel = elements.panels[side];
    const opened = active === side;
    elements.app.classList.toggle(`${side}-open`, opened);
    elements.triggers[side].setAttribute('aria-expanded', String(opened));
    panel.inert = elements.media.matches && !opened;
    if (elements.media.matches) {
      panel.setAttribute('aria-hidden', String(!opened));
      if (opened) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
      }
    } else {
      panel.removeAttribute('aria-hidden');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
    }
  }
  elements.reader.inert = !!active;
  elements.backdrop.hidden = !active;
  lockScroll(!!active);
}

/** On desktop this only focuses the requested field; both sidebars remain visible. */
export function openDrawer(side: DrawerSide, target?: HTMLElement) {
  if (!elements || hasModal()) return;
  if (!elements.media.matches) {
    if (target) {
      // Asking about a passage also reveals the companion in desktop focus mode.
      elements.app.classList.remove('focus');
      el('focusBtn').classList.remove('on');
      if (!target.matches(':disabled') && visible(target)) target.focus({ preventScroll: true });
    }
    return;
  }
  if (!active) {
    const focused = document.activeElement;
    opener = focused instanceof HTMLElement && focused !== document.body && visible(focused)
      ? focused : elements.triggers[side];
  }
  active = side;
  hideReadingPanels();
  sync();
  focusInside(target);
  // Inert/visibility changes can reject focus until the opening style is painted.
  // Keep the synchronous attempt for touch keyboards, then repair only lost focus.
  const request = ++focusRequest;
  const frame = window.requestAnimationFrame?.bind(window);
  const stillOpening = () => request === focusRequest && active === side && !hasModal();
  frame?.(() => {
    if (!stillOpening()) return;
    frame(() => {
      if (!stillOpening() || !elements) return;
      if (!elements.panels[side].contains(document.activeElement)) focusInside(target);
    });
  });
}

/** The optional side prevents a late navigation response from closing the other drawer. */
export function closeDrawer(side?: DrawerSide, options: CloseOptions = {}) {
  if (!elements || !active || (side && active !== side)) return;
  focusRequest++;
  const previous = active;
  active = null;
  sync();
  if (!hasModal()) {
    if (options.focusReader) {
      elements.reader.tabIndex = -1;
      elements.reader.focus({ preventScroll: true });
    } else if (options.restoreFocus !== false) {
      const target = opener && visible(opener) ? opener : elements.triggers[previous];
      if (visible(target)) target.focus({ preventScroll: true });
    }
  }
  opener = null;
}

export function initDrawers(onEscapeWithoutDrawer: () => void) {
  if (elements) return;
  elements = {
    app: el('app'),
    reader: el('reader'),
    backdrop: el('drawerBackdrop') as HTMLButtonElement,
    panels: { shelf: el('shelf'), companion: el('companion') },
    triggers: { shelf: el('shelfBtn') as HTMLButtonElement, companion: el('companionBtn') as HTMLButtonElement },
    closes: { shelf: el('shelfClose') as HTMLButtonElement, companion: el('companionClose') as HTMLButtonElement },
    media: window.matchMedia('(max-width: 920px)'),
  };
  const ui = elements;
  for (const side of SIDES) {
    ui.triggers[side].setAttribute('aria-controls', side);
    if (!ui.panels[side].hasAttribute('aria-label') && !ui.panels[side].hasAttribute('aria-labelledby')) {
      ui.panels[side].setAttribute('aria-label', side === 'shelf' ? '书库' : '一起读');
    }
    ui.triggers[side].addEventListener('click', () => {
      if (active === side) closeDrawer();
      else openDrawer(side);
    });
    ui.closes[side].addEventListener('click', () => closeDrawer(side));
  }
  ui.backdrop.tabIndex = -1;
  ui.backdrop.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeDrawer();
  });
  ui.media.addEventListener('change', () => {
    const focused = document.activeElement;
    closeDrawer(undefined, { restoreFocus: false });
    sync();
    if (hasModal()) return;
    // A desktop control can become hidden when crossing into the mobile layout.
    for (const side of SIDES) {
      if (ui.media.matches && focused instanceof HTMLElement && ui.panels[side].contains(focused)) {
        ui.triggers[side].focus({ preventScroll: true });
        break;
      }
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.defaultPrevented || event.isComposing || hasModal()) return;
    if (event.key === 'Escape') {
      if (active) {
        event.preventDefault();
        closeDrawer();
      } else if (!el('displayPanel').hidden) {
        event.preventDefault();
        hideReadingPanels();
        el('displayBtn').focus({ preventScroll: true });
      } else onEscapeWithoutDrawer();
      return;
    }
    if (event.key !== 'Tab' || !active) return;
    const nodes = focusables(ui.panels[active]);
    const first = nodes[0];
    const last = nodes.at(-1);
    if (!first || !last) {
      event.preventDefault();
      focusInside();
      return;
    }
    if (event.shiftKey && (document.activeElement === first || !ui.panels[active].contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !ui.panels[active].contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  });
  document.addEventListener('focusin', (event) => {
    if (!active || hasModal()) return;
    if (!(event.target instanceof Node) || !ui.panels[active].contains(event.target)) focusInside();
  });
  new MutationObserver(() => {
    if (!el('authMask').hidden) closeDrawer(undefined, { restoreFocus: false });
  }).observe(el('authMask'), { attributes: true, attributeFilter: ['hidden'] });
  sync();
}
